import type { ExplorerSourceSummary } from '../types';

interface SourceCatalogListProps {
  sources: ExplorerSourceSummary[];
  selectedSourceKey?: string | null;
  onSelect: (source: ExplorerSourceSummary) => void;
  emptyMessage?: string;
  className?: string;
}

export function SourceCatalogList({
  sources,
  selectedSourceKey = null,
  onSelect,
  emptyMessage = 'No sources match the current search.',
  className,
}: SourceCatalogListProps) {
  if (sources.length === 0) {
    return <p className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-4 py-6 text-sm text-slate-500">{emptyMessage}</p>;
  }

  return (
    <div className={`space-y-2 ${className ?? ''}`.trim()}>
      {sources.map((source) => {
        const isSelected = source.sourceKey === selectedSourceKey;

        return (
          <button
            key={source.sourceKey}
            type="button"
            onClick={() => onSelect(source)}
            className={`w-full rounded-2xl border px-4 py-3 text-left transition ${isSelected
              ? 'border-sky-300 bg-sky-50 shadow-sm ring-2 ring-sky-100'
              : 'border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50'}`}
          >
            <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-base font-semibold text-slate-900">{source.displayName}</span>
                  {source.superAdminOnly && (
                    <span className="rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-amber-800">
                      Super
                    </span>
                  )}
                </div>
                <p className="mt-1 text-sm text-slate-600">{source.description}</p>
              </div>

              <div className="flex flex-wrap items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                <span className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-[11px] text-slate-700">
                  {source.executionModeName}
                </span>
                <span className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-[11px] text-slate-700">
                  {source.databaseTypeName}
                </span>
              </div>
            </div>

            {(source.tags.length > 0 || source.groups.length > 0) && (
              <div className="mt-3 flex flex-wrap gap-2">
                {source.groups.map((group) => (
                  <span key={group.id} className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-[11px] font-semibold text-slate-700">
                    {group.label}
                  </span>
                ))}
                {source.tags.map((tag) => (
                  <span key={tag.id} className="rounded-full border border-slate-200 bg-white px-2.5 py-1 text-[11px] font-semibold text-slate-600">
                    {tag.label}
                  </span>
                ))}
              </div>
            )}
          </button>
        );
      })}
    </div>
  );
}

export default SourceCatalogList;